import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Ban, CheckCircle2, Database, GitBranch, Inbox, Plus, RotateCcw, Search, ShieldCheck, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCreateDataAsset, useListDataAssets, useDataAssetLifecycle, getListDataAssetsQueryKey } from "@tubo/api-models/hooks/data-asset-api.hooks";
import { useListModelVersions, useSemanticDataModelList } from "@tubo/api-models/hooks/semantic-data-model-api.hooks";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import { DataAssetStatusEnum } from "@tubo/api-models/business/data-asset/data-asset.enums";
import type { AssetListItemResp } from "@tubo/api-models/hooks/data-asset-api.schemas";
import { DeleteAssetDialog } from "@client/components/semantic-platform/data-assets/delete-asset-dialog";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";

export const Route = createFileRoute("/_app/semantic-platform/data-assets/")({
  // 领域上下文入 URL（缺省=全局；__all__=全部领域聚合浏览；经 modelId 服务端派生过滤）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: DataAssetListPage,
});

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<string, string> = {
  [DataAssetStatusEnum.Draft]: "草稿",
  [DataAssetStatusEnum.Building]: "建设中",
  [DataAssetStatusEnum.Publishable]: "可发布",
  [DataAssetStatusEnum.Published]: "已发布",
  [DataAssetStatusEnum.Suspended]: "已停用",
  [DataAssetStatusEnum.Archived]: "已归档",
};

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "状态：全部" },
  ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label: `状态：${label}` })),
];

const MODEL_STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  pendingReview: "待审批",
  published: "已发布",
  deprecated: "已废弃",
};

const ASSET_LIFECYCLE_STEPS = [
  { title: "选择模型版本", description: "确定实体、属性和关系结构", icon: GitBranch },
  { title: "运行数据抽取", description: "从文件中生成结构化结果包", icon: Inbox },
  { title: "接收并审核", description: "处理批次，确认实体与事实", icon: ShieldCheck },
  { title: "形成发布版本", description: "用变更集提交不可变版本", icon: CheckCircle2 },
  { title: "面向业务使用", description: "通过实体、视图和知识服务消费", icon: Database },
] as const;

function DataAssetListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [status, setStatus] = useState<string>("all");
  const [keyword, setKeyword] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AssetListItemResp | null>(null);
  const statusValues = Object.values(DataAssetStatusEnum);
  const selectedStatus = statusValues.find((v) => v === status);
  // 领域上下文（服务端派生过滤：经 modelId→模型→namespaceId）
  const { ns, nsFilter } = useNsContext();

  const { data, isLoading } = useListDataAssets(
    {
      page,
      pageSize: pageSize,
      ...(selectedStatus ? { status: selectedStatus } : {}),
      ...(keyword.trim() ? { q: keyword.trim() } : {}),
      ns: nsFilter,
    },
    { query: { placeholderData: (prev) => prev } },
  );

  const lifecycle = useDataAssetLifecycle({
    mutation: {
      onSuccess: async () => {
        toast.success("生命周期操作成功");
        await queryClient.invalidateQueries({ queryKey: getListDataAssetsQueryKey() });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : "操作失败"),
    },
  });

  const assets = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns = useMemo<DataTableColumn<AssetListItemResp>[]>(
    () => [
      { id: "code", header: "编码", cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
      { id: "name", header: "名称", cell: (row) => row.name },
      {
        id: "status",
        header: "状态",
        cell: (row) => (
          <Badge variant={row.status === DataAssetStatusEnum.Published ? "default" : "secondary"}>
            {STATUS_LABEL[row.status] ?? row.status}
          </Badge>
        ),
      },
      {
        id: "namespace",
        header: "所属领域",
        cell: (row) =>
          row.namespaceId ? (
            <Badge variant="outline">{row.namespaceName ?? row.namespaceId}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">全局</span>
          ),
      },
      { id: "modelVersion", header: "模型版本", cell: (row) => <span className="text-xs text-muted-foreground">{row.modelVersion}</span> },
      { id: "createdAt", header: "创建时间", cell: (row) => <span className="text-xs text-muted-foreground">{new Date(row.createdAt).toLocaleString()}</span> },
      {
        id: "actions",
        header: "操作",
        cell: (row) => (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {row.status === DataAssetStatusEnum.Published && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="停用"
                title="停用"
                disabled={lifecycle.isPending}
                onClick={() => lifecycle.mutate({ id: row.id, data: { action: "suspend", reason: "列表页停用" } })}
              >
                <Ban className="size-3.5" />
              </Button>
            )}
            {row.status === DataAssetStatusEnum.Suspended && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="恢复"
                title="恢复"
                disabled={lifecycle.isPending}
                onClick={() => lifecycle.mutate({ id: row.id, data: { action: "restore" } })}
              >
                <RotateCcw className="size-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="删除"
              title="删除(进回收站)"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteTarget(row)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [lifecycle],
  );

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">数据资产</span>,
    [],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Select items={STATUS_FILTER_OPTIONS} value={status} onValueChange={(value) => { setStatus(value ?? "all"); setPage(1); }}>
          <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="w-56 pl-8"
            placeholder="搜索名称或编码"
            value={keyword}
            onChange={(event) => { setKeyword(event.target.value); setPage(1); }}
          />
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" /> 新建资产
        </Button>
      </div>
    ),
    [keyword, status],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <AssetLifecycleGuide />
      <div className="min-h-0 flex-1">
        <DataTable
          columns={columns}
          data={assets}
          rowKey={(row) => row.id}
          page={page}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          total={total}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyState={
            <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
              <Database className="mb-3 h-12 w-12 text-icon-secondary-muted" />
              <p className="text-sm font-medium">暂无数据资产</p>
              <p className="mt-1 text-xs">先选择一个场景模型版本，创建资产骨架</p>
            </div>
          }
          onRowClick={(row) =>
            navigate({ to: "/semantic-platform/data-assets/$assetId", params: { assetId: row.id }, search: ns ? { ns } : {} })
          }
        />
      </div>

      <CreateAssetDialog open={createOpen} onOpenChange={setCreateOpen} />

      {deleteTarget && (
        <DeleteAssetDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeleteTarget(null);
          }}
          id={deleteTarget.id}
          name={deleteTarget.name}
        />
      )}
    </div>
  );
}

function AssetLifecycleGuide() {
  return (
    <section className="shrink-0 border-b bg-muted/15 px-4 py-3">
      <div className="flex flex-col gap-3 rounded-lg border bg-background px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">数据资产如何形成</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              创建只是建立资产骨架，数据要经过抽取、审核和发布后才成为可复用版本。
            </p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          {ASSET_LIFECYCLE_STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="relative flex gap-2.5 md:pr-3">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="size-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium">{step.title}</p>
                  <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{step.description}</p>
                </div>
                {index < ASSET_LIFECYCLE_STEPS.length - 1 && (
                  <ArrowRight className="absolute right-0 top-1.5 hidden size-3.5 text-muted-foreground/50 md:block" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CreateAssetDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate();
  const { ns } = useNsContext();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [modelId, setModelId] = useState("");
  const [modelVersion, setModelVersion] = useState("");

  const { data: models, isLoading: modelsLoading } = useSemanticDataModelList(undefined, {
    query: { enabled: open },
  });
  const { data: modelVersions, isLoading: versionsLoading } = useListModelVersions(modelId, {
    query: { enabled: open && Boolean(modelId) },
  });
  const selectedModel = models?.find((model) => model.id === modelId);
  const selectedVersion = modelVersions?.find((version) => version.version === modelVersion);
  const modelItems = useMemo(
    () => (models ?? []).map((model) => ({ value: model.id, label: `${model.name} · ${model.code}` })),
    [models],
  );
  const versionItems = useMemo(
    () => (modelVersions ?? []).map((version) => ({ value: version.version, label: `${version.version} · ${version.name}` })),
    [modelVersions],
  );

  useEffect(() => {
    if (!modelId || !modelVersions?.length) return;
    if (!modelVersions.some((version) => version.version === modelVersion)) {
      setModelVersion(modelVersions[0].version);
    }
  }, [modelId, modelVersion, modelVersions]);

  const resetForm = () => {
    setCode("");
    setName("");
    setModelId("");
    setModelVersion("");
  };

  const createAsset = useCreateDataAsset({
    mutation: {
      onSuccess: async (resp) => {
        toast.success("数据资产已创建");
        await queryClient.invalidateQueries({ queryKey: getListDataAssetsQueryKey() });
        resetForm();
        onOpenChange(false);
        navigate({ to: "/semantic-platform/data-assets/$assetId", params: { assetId: resp.id }, search: ns ? { ns } : {} });
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : "创建失败");
      },
    },
  });

  const valid = Boolean(code.trim() && name.trim() && modelId && modelVersion);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const submit = () => {
    if (!valid) {
      toast.error("请填写编码、名称并选择模型版本");
      return;
    }
    createAsset.mutate({ data: { code: code.trim(), name: name.trim(), modelId, modelVersion } });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>新建数据资产</DialogTitle>
          <DialogDescription>
            先绑定一个场景模型版本，后续抽取结果必须与该版本匹配。
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>资产编码</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="如 amorphous-alloy-papers" />
          </div>
          <div className="grid gap-1.5">
            <Label>资产名称</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="如 非晶合金文献实验数据" />
          </div>
          <div className="grid gap-1.5">
            <Label>场景模型 *</Label>
            <Select
              items={modelItems}
              value={modelId}
              onValueChange={(value) => {
                setModelId(value ?? "");
                setModelVersion("");
              }}
              disabled={modelsLoading || modelItems.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={modelsLoading ? "加载模型中…" : "选择场景模型"} />
              </SelectTrigger>
              <SelectContent>
                {(models ?? []).map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <span>{model.name}</span>
                    <span className="text-xs text-muted-foreground">{model.code} · {MODEL_STATUS_LABEL[model.status] ?? model.status}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!modelsLoading && modelItems.length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                暂无可用场景模型，请先创建并保存一个模型版本。
                <Link to="/semantic-platform/data-models" className="ml-1 text-primary hover:underline">去数据模型页</Link>
              </p>
            )}
            {selectedModel && (
              <p className="text-[11px] text-muted-foreground">
                已选择 {selectedModel.name}（{selectedModel.code}），模型 ID 已自动绑定。
              </p>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label>模型版本 *</Label>
            <Select
              items={versionItems}
              value={modelVersion}
              onValueChange={(value) => setModelVersion(value ?? "")}
              disabled={!modelId || versionsLoading || versionItems.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={versionsLoading ? "加载版本中…" : "选择模型版本"} />
              </SelectTrigger>
              <SelectContent>
                {(modelVersions ?? []).map((version) => (
                  <SelectItem key={version.id} value={version.version}>
                    <span>{version.version} · {version.name}</span>
                    <span className="text-xs text-muted-foreground">{version.publishStatus === "published" ? "已发布" : version.publishStatus === "pendingReview" ? "待审批" : "已保存"}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {modelId && !versionsLoading && versionItems.length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                该模型还没有版本快照，请先在模型页面保存或发布。
                <Link to="/semantic-platform/data-models/$id" params={{ id: modelId }} className="ml-1 text-primary hover:underline">去该模型页</Link>
              </p>
            )}
            {selectedVersion && (
              <p className="text-[11px] text-muted-foreground">
                版本快照于 {new Date(selectedVersion.frozenAt).toLocaleString()} 生成，资产数据结构将以此为准。
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>取消</Button>
          <Button onClick={submit} disabled={!valid || createAsset.isPending}>创建资产</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { STATUS_LABEL };
